import React, { useEffect, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AlertNotification = ({ alert, onDismiss }) => {
  const audioRef = useRef(null);

  useEffect(() => {
    // Play sound notification for vehicle detection
    if (alert?.type === 'success' && alert?.title?.includes('Vehículo')) {
      // Create and play success sound
      const playSuccessSound = () => {
        // Create AudioContext for generating success sound
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Create success sound pattern
        const playTone = (frequency, duration, delay = 0) => {
          setTimeout(() => {
            const oscillator = audioContext?.createOscillator();
            const gainNode = audioContext?.createGain();
            
            oscillator?.connect(gainNode);
            gainNode?.connect(audioContext?.destination);
            
            oscillator?.frequency?.setValueAtTime(frequency, audioContext?.currentTime);
            oscillator.type = 'sine';
            
            // Smooth envelope
            gainNode?.gain?.setValueAtTime(0, audioContext?.currentTime);
            gainNode?.gain?.linearRampToValueAtTime(0.1, audioContext?.currentTime + 0.05);
            gainNode?.gain?.exponentialRampToValueAtTime(0.01, audioContext?.currentTime + duration);
            
            oscillator?.start(audioContext?.currentTime);
            oscillator?.stop(audioContext?.currentTime + duration);
          }, delay);
        };
        
        // Play success melody (C-E-G chord pattern)
        playTone(523.25, 0.2, 0);    // C5
        playTone(659.25, 0.2, 150);  // E5
        playTone(783.99, 0.3, 250);  // G5
      };
      
      // Small delay to ensure component is rendered
      setTimeout(playSuccessSound, 100);
    }
  }, [alert]);

  if (!alert) return null;

  const getAlertStyles = () => {
    switch (alert?.type) {
      case 'error':
        return {
          container: 'bg-gradient-to-r from-red-50 to-red-100 border-red-200 text-red-900 shadow-lg shadow-red-100/50',
          icon: 'AlertCircle',
          iconColor: 'text-red-600',
          pulseColor: 'animate-pulse'
        };
      case 'warning':
        return {
          container: 'bg-gradient-to-r from-amber-50 to-yellow-100 border-amber-200 text-amber-900 shadow-lg shadow-amber-100/50',
          icon: 'AlertTriangle',
          iconColor: 'text-amber-600',
          pulseColor: 'animate-pulse'
        };
      case 'success':
        // Enhanced green styling for vehicle detections
        if (alert?.title?.includes('Vehículo') || alert?.title?.includes('Detectado')) {
          return {
            container: 'bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-300 text-emerald-900 shadow-2xl shadow-emerald-200/60 ring-2 ring-emerald-200/30',
            icon: 'CheckCircle2',
            iconColor: 'text-emerald-600',
            pulseColor: 'animate-pulse text-emerald-500',
            titleColor: 'text-emerald-800 font-bold',
            messageColor: 'text-emerald-700',
            accent: 'border-l-4 border-l-emerald-500'
          };
        }
        return {
          container: 'bg-gradient-to-r from-emerald-50 to-green-100 border-emerald-200 text-emerald-900 shadow-lg shadow-emerald-100/50',
          icon: 'CheckCircle',
          iconColor: 'text-emerald-600',
          pulseColor: 'animate-pulse'
        };
      default:
        return {
          container: 'bg-gradient-to-r from-slate-50 to-gray-100 border-slate-200 text-slate-900 shadow-lg',
          icon: 'Info',
          iconColor: 'text-slate-600',
          pulseColor: ''
        };
    }
  };

  const styles = getAlertStyles();

  return (
    <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md mx-4`}>
      <div className={`
        border rounded-xl p-5 backdrop-blur-sm
        ${styles?.container} ${styles?.accent || ''}
        animate-in slide-in-from-top-4 duration-500 ease-out
        hover:shadow-2xl transition-all duration-300
      `}>
        <div className="flex items-start gap-4">
          {/* Enhanced Icon with Pulse Animation */}
          <div className="flex-shrink-0 relative">
            <Icon 
              name={styles?.icon} 
              size={24} 
              className={`${styles?.iconColor} ${styles?.pulseColor} drop-shadow-sm`} 
            />
            {alert?.type === 'success' && alert?.title?.includes('Vehículo') && (
              <div className="absolute -inset-1 bg-emerald-400/20 rounded-full animate-ping"></div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                {alert?.title && (
                  <h4 className={`font-bold text-base mb-2 leading-tight ${styles?.titleColor || 'font-semibold'}`}>
                    {alert?.title}
                    {alert?.type === 'success' && alert?.title?.includes('Vehículo') && (
                      <span className="ml-2 inline-flex items-center text-xs bg-emerald-200 text-emerald-800 px-2 py-1 rounded-full font-medium">
                        🚗 NUEVO
                      </span>
                    )}
                  </h4>
                )}
                <p className={`text-sm leading-relaxed font-medium ${styles?.messageColor || 'opacity-90'}`}>
                  {alert?.message}
                </p>
                {alert?.timestamp && (
                  <p className="text-xs opacity-75 mt-2 font-mono">
                    {new Date(alert.timestamp)?.toLocaleString('es-MX', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </p>
                )}
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={onDismiss}
                iconName="X"
                iconSize={18}
                className="flex-shrink-0 h-8 w-8 opacity-60 hover:opacity-100 hover:bg-black/5 rounded-full transition-all duration-200"
              />
            </div>
          </div>
        </div>
        
        {/* Enhanced Progress Bar for Vehicle Detections */}
        {alert?.type === 'success' && alert?.title?.includes('Vehículo') && (
          <div className="mt-4 pt-3 border-t border-emerald-200/50">
            <div className="flex items-center gap-2 text-xs text-emerald-700">
              <Icon name="Activity" size={14} className="text-emerald-600" />
              <span className="font-medium">Procesando automáticamente...</span>
              <div className="flex-1 bg-emerald-200/50 rounded-full h-1.5 ml-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertNotification;